import React, { useState, useEffect } from 'react';
import {
  FaStore,
  FaMapMarkerAlt,
  FaUtensils,
  FaCheckCircle,
  FaTimes,
  FaSpinner,
  FaClock,
  FaReceipt,
  FaWallet,
  FaMinusCircle,
  FaInfoCircle
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getData, putData } from '../../../Services/Fetchnodeservices';

// ================= ZAPTODOOR ORANGE THEME =================
const palette = {
  saffron: '#f97316',
  chilli: '#dc2626',
  mintLeaf: '#10b981',
  turmeric: '#f59e0b',
  ash500: '#64748b',
};

const LiveOrdersTerminal = ({ isDark = false }) => {
  const navigate = useNavigate();
  
  // State
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState(null);
  
  // 🔥 Order Popup State
  const [orderPopup, setOrderPopup] = useState({ visible: false, orderData: null });

  // Theme configuration
  const T = {
    background: isDark ? '#0f172a' : '#fff7ed',
    card: isDark ? '#1e293b' : '#ffffff',
    textPrimary: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    borderLight: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
    shadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(249, 115, 22, 0.1)',
  };

  // Get user data from localStorage
  const getUserData = () => {
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const parsed = JSON.parse(userDataStr);
        setUser(parsed);
        return parsed;
      }
      return null;
    } catch (e) {
      console.log('Error parsing userData:', e);
      return null;
    }
  };

  // Load orders
  const loadOrders = async () => {
    const userData = getUserData();
    const restaurantId = userData?.id || userData?.restaurantId || localStorage.getItem('restaurantId');
    
    if (!restaurantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await getData(`orderRoutes/orders/pending/BUSINESS/${restaurantId}`);
      if (result.success) {
        const ordersData = result.data || [];
        setOrders(ordersData);
        
        const pendingOrders = ordersData.filter(
          order => order.order_status_restro === 'pending' || order.status === 'pending'
        );
        
        if (pendingOrders.length > 0 && !orderPopup.visible) {
          showOrderPopup(pendingOrders[0]);
        }
      } else {
        setOrders([]);
      }
    } catch (e) {
      console.log('Load orders error:', e);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const showOrderPopup = (order) => {
    const items = getOrderItems(order);
    const popupData = {
      id: order.id || order.order_id || '#ORD-' + Date.now(),
      customer: order.receiver_name || order.customer_name || order.user_name || 'Customer',
      address: order.dropoff_address || order.delivery_address || order.address || 'Address not available',
      items: items,
      total: order.total_amount || order.amount || order.total || 0,
      restaurantEarning: Number(order.business_amount || order.restaurant_earning || 0),
      estimatedTime: order.estimated_time || order.delivery_time || '20-30 min',
      orderType: order.order_type || order.type || 'delivery',
    };
    setOrderPopup({ visible: true, orderData: popupData });
  };

  const handlePopupAccept = async () => {
    const orderId = orderPopup.orderData?.id;
    if (!orderId) return;
    setProcessing(true);
    try {
      const result = await putData(`orderRoutes/order/accept/${orderId}`);
      if (result.success) {
        setOrderPopup({ visible: false, orderData: null });
        await loadOrders();
        alert('✅ Order Accepted Successfully!');
      } else {
        alert('❌ Failed to accept order: ' + (result.message || 'Unknown error'));
      }
    } catch (e) {
      alert('❌ Error accepting order');
    } finally {
      setProcessing(false);
    }
  };

  const handlePopupDecline = async () => {
    const orderId = orderPopup.orderData?.id;
    if (!orderId) return;
    setProcessing(true);
    try {
      const result = await putData(`orderRoutes/order/decline/${orderId}`);
      if (result.success) {
        setOrderPopup({ visible: false, orderData: null });
        await loadOrders();
        alert(result.refund_required ? '✅ Order declined. Customer refund initiated.' : '✅ Order declined successfully.');
      } else {
        alert('❌ Failed to decline order: ' + (result.message || 'Unknown error'));
      }
    } catch (e) {
      alert('❌ Error declining order');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    getUserData();
    loadOrders();
    const interval = setInterval(loadOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleAccept = async (id) => {
    if (processing) return;
    setProcessing(true);
    try {
      const result = await putData(`orderRoutes/order/accept/${id}`);
      if (result.success) {
        await loadOrders();
        alert('✅ Order Accepted Successfully!');
      } else {
        alert('❌ Failed to accept order');
      }
    } catch (e) {
      alert('❌ Error accepting order');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async (id) => {
    if (processing) return;
    setProcessing(true);
    try {
      const result = await putData(`orderRoutes/order/decline/${id}`);
      if (result.success) {
        await loadOrders();
        alert(result.refund_required ? '✅ Order declined. Customer refund initiated.' : '✅ Order declined successfully.');
      } else {
        alert('❌ Failed to decline order');
      }
    } catch (e) {
      alert('❌ Error declining order');
    } finally {
      setProcessing(false);
    }
  };

  const handleStartPreparing = async (id) => {
    if (processing) return;
    setProcessing(true);
    try {
      const result = await putData(`orderRoutes/order/preparing/${id}`);
      if (result.success) {
        await loadOrders();
        alert('✅ Order is now preparing!');
      } else {
        alert('❌ Failed to update order status');
      }
    } catch (e) {
      alert('❌ Error updating order');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkReady = async (id) => {
    if (processing) return;
    setProcessing(true);
    try {
      const result = await putData(`orderRoutes/order/ready/${id}`);
      if (result.success) {
        await loadOrders();
        alert('✅ Order ready for pickup!');
      } else {
        alert('❌ Failed to update order status');
      }
    } catch (e) {
      alert('❌ Error updating order');
    } finally {
      setProcessing(false);
    }
  };

  const getOrderItems = (order) => {
    if (!order?.items) return [];
    if (typeof order.items === 'string') {
      try { return JSON.parse(order.items); } catch { return []; }
    }
    if (Array.isArray(order.items)) return order.items;
    return [];
  };

  const renderOrderPopup = () => {
    if (!orderPopup.visible || !orderPopup.orderData) return null;
    const order = orderPopup.orderData;
    const displayItems = order.items.map((item) => {
      let itemName = 'Item', quantity = '1';
      if (typeof item === 'object' && item !== null) {
        itemName = item.name || item.item_name || item.product_name || item.title || 'Item';
        quantity = item.qty || item.quantity || item.count || item.amount || '1';
      } else if (typeof item === 'string') {
        const match = item.match(/^(\d+)x\s*(.+)$/);
        if (match) { quantity = match[1]; itemName = match[2]; }
        else { itemName = item; }
      }
      return { name: itemName, qty: quantity };
    });

    const popupStyles = {
      overlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(4px)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 99999, animation: 'fadeIn 0.3s ease',
      },
      container: {
        backgroundColor: T.card, borderRadius: '24px', padding: '28px',
        maxWidth: '460px', width: '92%', maxHeight: '90vh', overflowY: 'auto',
        position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        border: `1px solid ${T.border}`, animation: 'slideUp 0.4s ease',
      },
      close: {
        position: 'absolute', top: '16px', right: '16px', background: 'none',
        border: 'none', fontSize: '20px', cursor: 'pointer', color: T.textSecondary,
      },
      header: { textAlign: 'center', marginBottom: '20px' },
      icon: { fontSize: '40px', display: 'block', marginBottom: '8px' },
      title: { fontSize: '24px', fontWeight: '800', color: T.textPrimary, margin: '0 0 4px 0' },
      orderId: { fontSize: '14px', color: palette.saffron, fontWeight: '700', fontFamily: 'monospace' },
      customer: {
        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#fff7ed',
        borderRadius: '16px', padding: '16px', marginBottom: '16px',
        border: `1px solid ${T.border}`,
      },
      customerName: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' },
      name: { fontSize: '16px', fontWeight: '700', color: T.textPrimary },
      address: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: T.textSecondary, lineHeight: '1.4' },
      items: {
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
        borderRadius: '16px', padding: '16px', marginBottom: '16px',
      },
      itemsHeader: {
        display: 'flex', justifyContent: 'space-between', fontSize: '12px',
        fontWeight: '700', color: T.textSecondary, marginBottom: '10px',
        textTransform: 'uppercase', letterSpacing: '0.5px',
      },
      itemRow: {
        display: 'flex', justifyContent: 'space-between', padding: '6px 0',
        fontSize: '14px', color: T.textPrimary, borderBottom: `1px dashed ${T.borderLight}`,
      },
      earnings: {
        background: T.primaryGradient, borderRadius: '16px', padding: '16px',
        marginBottom: '20px', color: '#ffffff',
      },
      earningRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '14px' },
      earningAmount: { fontSize: '20px', fontWeight: '800', color: '#ffffff' },
      time: { fontWeight: '600', color: '#ffffff', opacity: 0.9 },
      actions: { display: 'flex', gap: '12px' },
      decline: {
        flex: 1, padding: '14px', borderRadius: '14px',
        border: `2px solid ${palette.chilli}`, backgroundColor: 'transparent',
        color: palette.chilli, fontWeight: '700', fontSize: '15px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
      },
      accept: {
        flex: 2, padding: '14px', borderRadius: '14px', border: 'none',
        background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
        fontWeight: '700', fontSize: '15px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)', transition: 'all 0.2s',
      },
    };

    return (
      <div style={popupStyles.overlay}>
        <div style={popupStyles.container}>
          <button style={popupStyles.close} onClick={() => setOrderPopup({ visible: false, orderData: null })}>
            <FaTimes />
          </button>
          <div style={popupStyles.header}>
            <span style={popupStyles.icon}>🍽️</span>
            <h2 style={popupStyles.title}>New Order!</h2>
            <span style={popupStyles.orderId}>#{order.id}</span>
          </div>
          <div style={popupStyles.customer}>
            <div style={popupStyles.customerName}>
              <span style={{ fontSize: '20px' }}>👤</span>
              <span style={popupStyles.name}>{order.customer}</span>
            </div>
            <div style={popupStyles.address}>
              <FaMapMarkerAlt color={palette.saffron} style={{ marginTop: '3px' }} />
              <span>{order.address}</span>
            </div>
          </div>
          <div style={popupStyles.items}>
            <div style={popupStyles.itemsHeader}>
              <span>Order Items</span>
              <span>Qty</span>
            </div>
            {displayItems.map((item, index) => (
              <div key={index} style={popupStyles.itemRow}>
                <span>{item.name}</span>
                <span style={{ fontWeight: '600' }}>{item.qty}</span>
              </div>
            ))}
          </div>
          <div style={popupStyles.earnings}>
            <div style={popupStyles.earningRow}>
              <span>Restaurant Earning</span>
              <span style={popupStyles.earningAmount}>₹{Number(order.restaurantEarning || 0).toFixed(2)}</span>
            </div>
            <div style={popupStyles.earningRow}>
              <span>Estimated Time</span>
              <span style={popupStyles.time}>⏱️ {order.estimatedTime}</span>
            </div>
          </div>
          <div style={popupStyles.actions}>
            <button 
              style={popupStyles.decline} onClick={handlePopupDecline} disabled={processing}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = palette.chilli; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = palette.chilli; }}
            >
              {processing ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Decline'}
            </button>
            <button 
              style={popupStyles.accept} onClick={handlePopupAccept} disabled={processing}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {processing ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Accept Order'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ================= REDESIGNED ORDER CARD =================
  const OrderCard = ({ item }) => {
    const items = getOrderItems(item);
    const status = item.order_status_restro || 'pending';
    const isPending = status === 'pending';
    const isAccepted = status === 'accepted';
    const isPreparing = status === 'preparing';
    const isReady = status === 'ready';
    
    const statusConfig = {
      pending: { color: palette.chilli, label: 'NEW', bg: isDark ? 'rgba(220, 38, 38, 0.15)' : '#fef2f2' },
      accepted: { color: palette.saffron, label: 'ACCEPTED', bg: isDark ? 'rgba(249, 115, 22, 0.15)' : '#fff7ed' },
      preparing: { color: '#3b82f6', label: 'PREPARING', bg: isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff' },
      ready: { color: palette.mintLeaf, label: 'READY', bg: isDark ? 'rgba(16, 185, 129, 0.15)' : '#f0fdf4' },
    }[status] || { color: palette.ash500, label: 'UNKNOWN', bg: T.borderLight };

    return (
      <div 
        style={{ 
          ...styles.card, 
          backgroundColor: T.card, 
          borderColor: T.border,
          borderLeft: `5px solid ${statusConfig.color}`,
          boxShadow: T.shadow 
        }} 
        onClick={() => { setSelectedOrder(item); setShowDetailModal(true); }}
      >
        <div style={styles.cardHeader}>
          <div>
            <span style={styles.orderId}>#{item.id}</span>
            <h4 style={{ ...styles.customerName, color: T.textPrimary }}>{item.receiver_name || 'Customer'}</h4>
          </div>
          <div style={{ ...styles.statusBadge, backgroundColor: statusConfig.bg }}>
            <span style={{ ...styles.statusDot, backgroundColor: statusConfig.color }} />
            <span style={{ ...styles.statusText, color: statusConfig.color }}>{statusConfig.label}</span>
          </div>
        </div>

        <div style={styles.addressRow}>
          <FaMapMarkerAlt size={14} color={palette.saffron} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span style={{ ...styles.addressText, color: T.textSecondary }}>{item.dropoff_address || 'No address provided'}</span>
        </div>

        <div style={{ ...styles.divider, borderColor: T.borderLight }} />

        <div style={styles.itemsContainer}>
          <FaUtensils size={14} color={palette.saffron} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span style={{ ...styles.itemsText, color: T.textSecondary }}>
            {items.map(i => `${i.qty || i.quantity || 1}x ${i.name}`).join(', ')}
          </span>
        </div>

        <div style={{ ...styles.earningBox, backgroundColor: isDark ? 'rgba(16, 185, 129, 0.1)' : '#f0fdf4', borderColor: isDark ? 'rgba(16, 185, 129, 0.2)' : '#bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaWallet size={14} color={palette.mintLeaf} />
            <span style={{ ...styles.payoutLabel, color: palette.mintLeaf }}>Restaurant Earning</span>
          </div>
          <span style={{ ...styles.payoutAmount, color: palette.mintLeaf }}>₹{Number(item.business_amount || 0).toFixed(2)}</span>
        </div>

        <button 
          style={styles.breakdownLinkBtn}
          onClick={(e) => { e.stopPropagation(); setSelectedOrder(item); setShowBreakdownModal(true); }}
        >
          <span style={{ ...styles.breakdownLinkText, color: palette.saffron }}>View Price Breakdown →</span>
        </button>

        <div style={styles.actionRow}>
          {isPending && (
            <>
              <button 
                style={{ ...styles.declineBtn, borderColor: palette.chilli, color: palette.chilli, backgroundColor: isDark ? 'rgba(220, 38, 38, 0.1)' : '#fef2f2' }} 
                onClick={(e) => { e.stopPropagation(); handleDecline(item.id); }} disabled={processing}
              >
                {processing ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} /> : 'Decline'}
              </button>
              <button 
                style={styles.acceptBtn} 
                onClick={(e) => { e.stopPropagation(); handleAccept(item.id); }} disabled={processing}
              >
                <div style={{ ...styles.gradBtn, background: T.primaryGradient }}>
                  {processing ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite', color: '#fff' }} /> : 'ACCEPT'}
                </div>
              </button>
            </>
          )}
          {isAccepted && (
            <button style={styles.readyBtn} onClick={(e) => { e.stopPropagation(); handleStartPreparing(item.id); }} disabled={processing}>
              <div style={{ ...styles.gradBtn, background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                {processing ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite', color: '#fff' }} /> : 'START PREPARING'}
              </div>
            </button>
          )}
          {isPreparing && (
            <button style={styles.readyBtn} onClick={(e) => { e.stopPropagation(); handleMarkReady(item.id); }} disabled={processing}>
              <div style={{ ...styles.gradBtn, background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                {processing ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite', color: '#fff' }} /> : 'MARK READY'}
              </div>
            </button>
          )}
          {isReady && (
            <div style={{ ...styles.completedBanner, backgroundColor: isDark ? 'rgba(16, 185, 129, 0.1)' : '#f0fdf4' }}>
              <FaCheckCircle size={18} color={palette.mintLeaf} />
              <span style={{ ...styles.completedText, color: palette.mintLeaf }}>Ready for pickup</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading && orders.length === 0) {
    return (
      <div style={{ ...styles.loadingContainer, backgroundColor: T.background }}>
        <FaSpinner style={{ ...styles.spinner, animation: 'spin 0.8s linear infinite', color: palette.saffron }} />
        <p style={{ color: T.textSecondary }}>Loading live orders...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, backgroundColor: T.background }}>
      {renderOrderPopup()}

      <div style={{ ...styles.header, borderBottomColor: T.border }}>
        <div>
          <h2 style={{ ...styles.headerTitle, color: T.textPrimary }}>Order Terminal</h2>
          <div style={styles.liveContainer}>
            <span style={styles.pulseDot} />
            <span style={{ ...styles.liveText, color: palette.mintLeaf }}>LIVE ORDERS</span>
          </div>
        </div>
        <button style={{ ...styles.circle, backgroundColor: T.card, borderColor: T.border }} onClick={() => navigate('/dashboard')}>
          <FaStore size={20} color={palette.saffron} />
        </button>
      </div>

      <div style={styles.list}>
        {orders.length === 0 ? (
          <div style={styles.emptyState}>
            <FaCheckCircle size={56} color={palette.mintLeaf} style={{ opacity: 0.5 }} />
            <h3 style={{ color: T.textPrimary, fontSize: '20px', fontWeight: '700' }}>All Caught Up!</h3>
            <p style={{ color: T.textSecondary, fontSize: '14px' }}>No pending orders at the moment.</p>
          </div>
        ) : (
          orders.map((item) => <OrderCard key={item.id} item={item} />)
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
          <div style={{ ...styles.modalContent, backgroundColor: T.card, borderColor: T.border }} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalCloseBtn} onClick={() => setShowDetailModal(false)}>
              <FaTimes size={20} color={T.textSecondary} />
            </button>
            <h3 style={{ ...styles.modalCustomer, color: T.textPrimary }}>{selectedOrder.receiver_name}</h3>
            <p style={{ ...styles.modalOrderId, color: palette.saffron }}>Order #{selectedOrder.id}</p>
            <div style={{ ...styles.divider, borderColor: T.borderLight }} />
            <div style={styles.modalItemsContainer}>
              {getOrderItems(selectedOrder).map((it, idx) => (
                <div key={idx} style={{ ...styles.modalItemRow, borderColor: T.borderLight }}>
                  <span style={{ ...styles.modalItemName, color: T.textPrimary }}>{it.qty || it.quantity || 1}x {it.name}</span>
                  <span style={{ ...styles.modalItemPrice, color: palette.mintLeaf }}>₹{(it.price || 0) * (it.qty || it.quantity || 1)}</span>
                </div>
              ))}
            </div>
            <button style={{ ...styles.closeModalBtn, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9', color: T.textPrimary }} onClick={() => setShowDetailModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ================= REDESIGNED BREAKDOWN MODAL ================= */}
      {showBreakdownModal && selectedOrder && (
        <div style={styles.modalOverlay} onClick={() => setShowBreakdownModal(false)}>
          <div style={{ ...styles.breakdownModalContent, backgroundColor: T.card, borderColor: T.border }} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalCloseBtn} onClick={() => setShowBreakdownModal(false)}>
              <FaTimes size={20} color={T.textSecondary} />
            </button>
            
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ 
                width: '48px', height: '48px', borderRadius: '50%', 
                backgroundColor: isDark ? 'rgba(249, 115, 22, 0.15)' : '#fff7ed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 12px'
              }}>
                <FaReceipt size={24} color={palette.saffron} />
              </div>
              <h3 style={{ ...styles.breakdownTitle, color: T.textPrimary, margin: 0 }}>Price Breakdown</h3>
              <p style={{ color: T.textSecondary, fontSize: '13px', marginTop: '4px' }}>Order #{selectedOrder.id}</p>
            </div>
            
            <div style={{ padding: '0 8px' }}>
              {/* Items Total */}
              <div style={styles.breakdownRow}>
                <span style={{ ...styles.breakdownLabel, color: T.textSecondary }}>Subtotal (Items)</span>
                <span style={{ ...styles.breakdownValue, color: T.textPrimary }}>₹{Number(selectedOrder.price || 0).toFixed(2)}</span>
              </div>

              {/* Platform Fee */}
              {Number(selectedOrder.platform_fee_amount || 0) > 0 && (
                <div style={styles.breakdownRow}>
                  <span style={{ ...styles.breakdownLabel, color: T.textSecondary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaStore size={12} /> Platform Fee
                  </span>
                  <span style={{ ...styles.breakdownValue, color: palette.chilli }}>-₹{Number(selectedOrder.platform_fee_amount || 0).toFixed(2)}</span>
                </div>
              )}

              {/* Commission */}
              {Number(selectedOrder.commission_amount || 0) > 0 && (
                <div style={styles.breakdownRow}>
                  <span style={{ ...styles.breakdownLabel, color: T.textSecondary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaMinusCircle size={12} /> Commission
                  </span>
                  <span style={{ ...styles.breakdownValue, color: palette.chilli }}>-₹{Number(selectedOrder.commission_amount || 0).toFixed(2)}</span>
                </div>
              )}

              {/* Handling Charges */}
              {Number(selectedOrder.handling_fee_amount || 0) > 0 && (
                <div style={styles.breakdownRow}>
                  <span style={{ ...styles.breakdownLabel, color: T.textSecondary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaUtensils size={12} /> Handling Charges
                  </span>
                  <span style={{ ...styles.breakdownValue, color: palette.chilli }}>-₹{Number(selectedOrder.handling_fee_amount || 0).toFixed(2)}</span>
                </div>
              )}

              {/* Order Receiving Charges */}
              {Number(selectedOrder.receiving_fee_amount || 0) > 0 && (
                <div style={styles.breakdownRow}>
                  <span style={{ ...styles.breakdownLabel, color: T.textSecondary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaInfoCircle size={12} /> Order Receiving
                  </span>
                  <span style={{ ...styles.breakdownValue, color: palette.chilli }}>-₹{Number(selectedOrder.receiving_fee_amount || 0).toFixed(2)}</span>
                </div>
              )}

              {/* Dashed Divider */}
              <div style={{ 
                height: '1px', 
                borderTop: `2px dashed ${T.border}`, 
                margin: '16px 0' 
              }} />

              {/* Your Earning Total */}
              <div style={{ ...styles.breakdownRow, ...styles.breakdownTotalRow }}>
                <span style={{ ...styles.breakdownTotalLabel, color: T.textPrimary }}>Your Earning</span>
                <span style={{ ...styles.breakdownTotalValue, color: palette.mintLeaf }}>
                  ₹{Number(selectedOrder.business_amount || 0).toFixed(2)}
                </span>
              </div>
            </div>

            <button 
              style={{ ...styles.closeBreakdownBtn, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9', color: T.textPrimary }} 
              onClick={() => setShowBreakdownModal(false)}
            >
              Close Breakdown
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
};

// ================= REDESIGNED STYLES =================
const styles = {
  container: {
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    transition: 'background-color 0.3s ease',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '16px',
  },
  spinner: { fontSize: '32px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    transition: 'border-color 0.3s ease',
  },
  headerTitle: { fontSize: '24px', fontWeight: '800', margin: 0 },
  liveContainer: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' },
  pulseDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: '#10b981', display: 'inline-block',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  liveText: { fontSize: '11px', fontWeight: '800', letterSpacing: '1px' },
  circle: {
    width: '44px', height: '44px', borderRadius: '50%',
    borderWidth: '1px', borderStyle: 'solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.2s ease',
  },
  list: { padding: '20px 24px', paddingBottom: '40px', maxWidth: '700px', margin: '0 auto' },
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '80px 20px', gap: '16px',
  },
  // Premium Order Card
  card: {
    borderRadius: '20px',
    padding: '20px',
    marginBottom: '16px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderLeftWidth: '5px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  orderId: {
    fontSize: '12px', fontWeight: '800', color: '#f97316',
    fontFamily: 'monospace', letterSpacing: '0.5px',
  },
  customerName: { fontSize: '17px', fontWeight: '800', margin: '4px 0 0 0' },
  statusBadge: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '4px 10px', borderRadius: '20px',
  },
  statusDot: { width: '6px', height: '6px', borderRadius: '50%', display: 'inline-block' },
  statusText: { fontSize: '10px', fontWeight: '800', letterSpacing: '0.5px' },
  addressRow: { display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px' },
  addressText: {
    flex: 1, fontSize: '13px', fontWeight: '500',
    lineHeight: '1.4',
  },
  divider: { height: '1px', margin: '14px 0', borderWidth: '1px', borderStyle: 'dashed' },
  itemsContainer: { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' },
  itemsText: { flex: 1, fontSize: '13px', fontWeight: '600', lineHeight: '1.4' },
  earningBox: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px', borderRadius: '12px',
    borderWidth: '1px', borderStyle: 'solid',
    marginBottom: '12px',
  },
  payoutLabel: { fontSize: '12px', fontWeight: '700' },
  payoutAmount: { fontSize: '16px', fontWeight: '800' },
  breakdownLinkBtn: {
    marginTop: '4px', background: 'none', border: 'none',
    cursor: 'pointer', padding: '8px 0', textAlign: 'left',
  },
  breakdownLinkText: { fontSize: '12px', fontWeight: '700' },
  actionRow: { marginTop: '16px', display: 'flex', gap: '12px' },
  declineBtn: {
    flex: 1, height: '48px', borderRadius: '12px',
    borderWidth: '2px', borderStyle: 'solid',
    fontWeight: '700', fontSize: '14px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  acceptBtn: { flex: 1, height: '48px', borderRadius: '12px', overflow: 'hidden', border: 'none', cursor: 'pointer' },
  readyBtn: { flex: 1, height: '48px', borderRadius: '12px', overflow: 'hidden', border: 'none', cursor: 'pointer', width: '100%' },
  gradBtn: {
    width: '100%', height: '100%', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    color: '#ffffff', fontWeight: '800', fontSize: '14px', gap: '8px',
  },
  completedBanner: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', height: '48px', borderRadius: '12px',
  },
  completedText: { fontSize: '14px', fontWeight: '800' },
  // Modals
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    zIndex: 1000, display: 'flex', alignItems: 'flex-end',
    justifyContent: 'center', padding: '20px',
  },
  modalContent: {
    borderTopLeftRadius: '24px', borderTopRightRadius: '24px',
    padding: '28px', maxWidth: '500px', width: '100%',
    maxHeight: '80vh', overflow: 'auto', position: 'relative',
    borderWidth: '1px', borderStyle: 'solid',
  },
  modalCloseBtn: {
    position: 'absolute', top: '16px', right: '16px',
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '8px', borderRadius: '50%', transition: 'all 0.2s ease',
  },
  modalCustomer: { fontSize: '22px', fontWeight: '800', margin: '8px 0 0 0' },
  modalOrderId: { fontSize: '14px', fontWeight: '700', margin: '4px 0 16px 0' },
  modalItemsContainer: { maxHeight: '300px', overflow: 'auto' },
  modalItemRow: {
    display: 'flex', justifyContent: 'space-between',
    padding: '12px 0', borderBottomWidth: '1px', borderBottomStyle: 'solid',
  },
  modalItemName: { fontSize: '14px', fontWeight: '600' },
  modalItemPrice: { fontSize: '14px', fontWeight: '800' },
  closeModalBtn: {
    marginTop: '24px', height: '50px', borderRadius: '14px',
    border: 'none', width: '100%', cursor: 'pointer',
    fontSize: '15px', fontWeight: '700', transition: 'all 0.2s ease',
  },
  // Premium Breakdown Modal
  breakdownModalContent: {
    borderTopLeftRadius: '24px', borderTopRightRadius: '24px',
    padding: '28px', maxWidth: '500px', width: '100%',
    maxHeight: '85vh', overflow: 'auto', position: 'relative',
    borderWidth: '1px', borderStyle: 'solid',
  },
  breakdownTitle: { fontSize: '20px', fontWeight: '800' },
  breakdownRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '10px 0',
  },
  breakdownLabel: { fontSize: '14px', fontWeight: '600' },
  breakdownValue: { fontSize: '14px', fontWeight: '700' },
  breakdownTotalRow: { marginTop: '8px' },
  breakdownTotalLabel: { fontSize: '16px', fontWeight: '800' },
  breakdownTotalValue: { fontSize: '20px', fontWeight: '900' },
  closeBreakdownBtn: {
    marginTop: '24px', height: '50px', borderRadius: '14px',
    border: 'none', width: '100%', cursor: 'pointer',
    fontSize: '15px', fontWeight: '700', transition: 'all 0.2s ease',
  },
};

export default LiveOrdersTerminal;